import type { KarrotLocalCountryCode } from "@karrotmarket-com/core";
import { parseKarrotLang } from "shared/parseKarrotLang";

import { getLocale } from "../i18n";
import getFleamarketArticlePrice from "../utils/getFleamarketArticlePrice";

export type SortOption = {
  label: string;
  urlFriendlyValue: string;
};

export type PriceInputRange = {
  from?: string;
  to?: string;
};

export type PriceOption = {
  label: string;
  value: Required<PriceInputRange>;
  urlFriendlyValue: string;
};

export type StaticFilterOptions = {
  sortOptions: SortOption[];
  priceOptions: PriceOption[];
};

const priceChipOptions = {
  jp: ["0.0", "500", "1000", "2000"],
  kr: ["0.0", "5000", "10000", "20000"],
  ca: ["0.0", "5", "10", "20"],
  us: ["0.0", "5", "10", "20"],
  uk: ["0.0", "5", "10", "20"],
} as const;

function getFreePrice(
  karrotLocalCountryCode: KarrotLocalCountryCode,
  price: string,
) {
  return {
    label: getFleamarketArticlePrice(karrotLocalCountryCode, price),
    value: {
      from: "0",
      to: "0",
    },
    urlFriendlyValue: "0__0",
  };
}

function getLocaledStaticPrice(
  karrotLocalCountryCode: KarrotLocalCountryCode,
): PriceOption[] {
  const prices = priceChipOptions[karrotLocalCountryCode];

  switch (karrotLocalCountryCode) {
    case "jp":
      return prices.map((price) => {
        if (price === "0.0") {
          return getFreePrice(karrotLocalCountryCode, price);
        }

        return {
          label: `${getFleamarketArticlePrice(
            karrotLocalCountryCode,
            price,
          )} 以下`,
          value: {
            from: "0",
            to: price,
          },
          urlFriendlyValue: `0__${price}`,
        };
      });
    case "kr":
      return prices.map((price) => {
        if (price === "0.0") {
          return getFreePrice(karrotLocalCountryCode, price);
        }

        return {
          label: `${getFleamarketArticlePrice(
            karrotLocalCountryCode,
            price,
          )} 이하`,
          value: {
            from: "0",
            to: price,
          },
          urlFriendlyValue: `0__${price}`,
        };
      });
    case "ca":
    case "uk":
    default:
      return prices.map((price) => {
        if (price === "0.0") {
          return getFreePrice(karrotLocalCountryCode, price);
        }

        return {
          label: `Under ${getFleamarketArticlePrice(
            karrotLocalCountryCode,
            price,
          )}`,
          value: {
            from: "0",
            to: price,
          },
          urlFriendlyValue: `0__${price}`,
        };
      });
  }
}

export function getStaticFilterOptions({
  karrotLocalCountryCode,
}: {
  karrotLocalCountryCode: KarrotLocalCountryCode;
}): StaticFilterOptions {
  const { t } = getLocale(parseKarrotLang(karrotLocalCountryCode));

  return {
    sortOptions: [
      {
        label: t("AsideFilter.sort_filter_most_recent"),
        urlFriendlyValue: "recent",
      },
      {
        label: t("AsideFilter.sort_filter_hottest"),
        urlFriendlyValue: "hottest",
      },
    ],
    priceOptions: getLocaledStaticPrice(karrotLocalCountryCode),
  };
}
